import Box, { BoxProps } from '@mui/material/Box'
import { styled } from '@mui/material/styles'
import React from 'react'
import { useNavigate } from 'react-router-dom-v5-compat'

import Loader from '@/app/component/atom/loader'
import Navigator, { TNavigatorItem } from '@/app/component/atom/navigator'
import SidebarLayout from '@/app/component/layout/sidebar-layout'
import { Permissions, UserRole } from '@/app/types'

import AuditLogTab from './audit-log'
import CustomFieldsTab from './custom-fields'
import CxUploadsTab from './cx-uploads'
import OrganizationInfoTab from './organization-info'
import PrivacyDataTab from './privacy-data'
import SubscriptionTab from './subscription'
import UsersTab from './users'

// Define the organization tabs, also used for routing (e.g., /organization/info)
export enum OrganizationTab {
  Info = 'info',
  Subscription = 'subscription',
  Privacy = 'privacy',
  Users = 'users',
  Audit = 'audit',
  CxUploads = 'cx-uploads',
  CustomFields = 'custom-fields',
}

type Props = {
  loading: boolean
  orgId: number
  permissions: Permissions
  role: UserRole
  tabName: OrganizationTab
  token: string
  userId: number

  downloadAuditLog: (params: { orgId: number; token: string; from: string; to: string }) => Promise<void>
}

const navigatorItems: TNavigatorItem<string>[] = [
  {
    icon: 'info',
    label: 'Info',
    path: OrganizationTab.Info,
  },
  {
    icon: 'subscriptions',
    label: 'Subscription Plan',
    path: OrganizationTab.Subscription,
  },
  {
    icon: 'privacy_tip',
    label: 'Privacy & Data',
    path: OrganizationTab.Privacy,
  },
  {
    icon: 'group',
    label: 'Users',
    path: OrganizationTab.Users,
  },
  {
    icon: 'receipt_long',
    label: 'Audit Log',
    path: OrganizationTab.Audit,
  },
  {
    icon: 'upload_file',
    label: 'Support Uploads',
    path: OrganizationTab.CxUploads,
  },
  {
    icon: 'bookmark',
    label: 'Custom Fields',
    path: OrganizationTab.CustomFields,
  },
]

const OrganizationComponent: React.FC<Props> = (props) => {
  const navigate = useNavigate()
  const { loading, tabName } = props

  const handleItemClick = React.useCallback(
    (tab: string) => {
      navigate(`/organization/${tab}`)
    },
    [navigate],
  )

  const sidebar = (
    <Navigator
      activePath={tabName}
      id="organization"
      items={navigatorItems}
      title="Organization"
      onItemClick={handleItemClick}
    />
  )

  const content = (
    <Container id={`org-content-${tabName}`} data-testid="organization-content" tabName={tabName}>
      {tabName === OrganizationTab.Info && <OrganizationInfoTab orgId={props.orgId} />}
      {tabName === OrganizationTab.Subscription && <SubscriptionTab />}
      {tabName === OrganizationTab.Privacy && <PrivacyDataTab />}
      {tabName === OrganizationTab.Users && <UsersTab {...props} />}
      {tabName === OrganizationTab.Audit && <AuditLogTab {...props} />}
      {tabName === OrganizationTab.CxUploads && <CxUploadsTab />}
      {tabName === OrganizationTab.CustomFields && <CustomFieldsTab />}
    </Container>
  )

  return (
    <>
      <SidebarLayout sidebar={sidebar} content={content} />
      {loading && <Loader />}
    </>
  )
}

const innerScrollingTabs = [OrganizationTab.Users, OrganizationTab.CustomFields, OrganizationTab.CxUploads]

const Container = styled(Box)<BoxProps & { tabName: OrganizationTab }>(({ tabName }) => ({
  ...(innerScrollingTabs.includes(tabName) && {
    height: '100%',
    width: '100%',
  }),
}))

export default OrganizationComponent
